/**
 * Orchestrator hook for sync Select — composes:
 * - useSelectCommonBehavior: open, dismiss, keyboard (shared with async)
 * - useAddItemOptions: search term, custom option injection, OptionComponent
 *
 * Adds: input placeholder logic and multi-select wrapper styling.
 */
import { useCallback, useMemo } from 'react';

import { useSelectCommonBehavior } from '../useSelectCommonBehavior';
import type { UseSyncOptionsParams, UseSyncOptionsReturn } from './types';
import { useAddItemOptions } from './useAddItemOptions';

export const useSyncOptions = <isMulti extends boolean = false, T = undefined>({
  options,
  isMulti,
  value,
  placeholder,
  defaultSearchTerm,
  debounceSearchDelay,
  allowAddItem,
  addItemLabel,
  onSearchChange: onSearchChangeProp,
  OptionComponent: propsOptionComponent,
  disabled,
  onBlur,
  multiSelectOverflow,
  hasMaxHeight,
  maxHeight,
}: UseSyncOptionsParams<isMulti, T>): UseSyncOptionsReturn<T> => {
  const { open, setOpen, handleDismiss, handleTriggerKeyDown } =
    useSelectCommonBehavior({
      disabled,
      onBlur,
    });

  const {
    resolvedOptions,
    debouncedSearchTerm,
    showAddItemOption,
    onSearchChange: addItemOnSearchChange,
    OptionComponent,
  } = useAddItemOptions({
    options,
    isMulti,
    defaultSearchTerm,
    debounceSearchDelay,
    allowAddItem,
    addItemLabel,
    onSearchChange: onSearchChangeProp,
    OptionComponent: propsOptionComponent,
  });

  // Wrap to pass current open state — useAddItemOptions only tracks
  // search term when the dropdown is open and allowAddItem is active.
  const onSearchChange = useCallback(
    (newSearchTerm?: string) => {
      addItemOnSearchChange(newSearchTerm, open);
    },
    [addItemOnSearchChange, open],
  );

  const inputPlaceholder = useMemo(() => {
    if (!isMulti) {
      return placeholder;
    }
    return (value as string[] | undefined)?.length ? undefined : placeholder;
  }, [placeholder, isMulti, value]);

  const wrapperStyle = useMemo(() => {
    if (!isMulti || multiSelectOverflow !== 'overflowStack' || !hasMaxHeight) {
      return undefined;
    }

    return {
      '--chips-max-height': `${maxHeight}px`,
    } as React.CSSProperties & { '--chips-max-height': string };
  }, [isMulti, multiSelectOverflow, hasMaxHeight, maxHeight]);

  return {
    open,
    setOpen,
    resolvedOptions,
    inputPlaceholder,
    debouncedSearchTerm,
    showAddItemOption,
    onSearchChange,
    handleDismiss,
    handleTriggerKeyDown,
    OptionComponent,
    wrapperStyle,
  };
};
